import { useRollbar } from '@rollbar/react'
import { isOneShot } from 'common/components/entities/OfferPriceNew'
import { BadRequest, InternalError, NetworkError } from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { ProductInterface } from 'common/types/entities/ProductInterface'
import { TwoStepPaymentFormStepInterface } from 'common/types/entities/two-step-payment-form-interface'
import { PricePlan } from 'common/types/OfferInterface'
import {
  buyMainOffer,
  RazorpayBuyOfferResponse,
} from 'publisher/api/razorpayApi'
import usePaymentSubmit from 'publisher/hooks/payment/usePaymentSubmit'
import useRazorpay, {
  RazorpayOptions,
} from 'publisher/hooks/payment/useRazorpay'
import { usePage, usePayment } from 'publisher/store'
import { getPageId } from 'publisher/store/page/pageSelectors'
import paymentSelectors, {
  getActivePricePlan,
  getCheckedBump,
  getPurchaseProcessId,
} from 'publisher/store/payment/paymentSelectors'
import React, { useEffect, useState } from 'react'
import { PaymentButtonEntityInterface } from 'src/shared/entities/payment-button/payment-button.schema'

const isHundredPercentCouponBasedOnMainOfferResponse = (
  pricePlan: PricePlan | null | undefined,
  product: ProductInterface | null,
  mainPurchaseResponse: RazorpayBuyOfferResponse,
) => {
  if (
    ((pricePlan && isOneShot(pricePlan)) || product) &&
    !mainPurchaseResponse.rpOrderId
  ) {
    return true
  }
  if (
    pricePlan &&
    !isOneShot(pricePlan) &&
    !mainPurchaseResponse.rpSubscriptionId
  ) {
    return true
  }
  return false
}

export default function useRazorpayPaymentHandler(
  entity:
    | OldEntityInterface
    | PaymentButtonEntityInterface
    | BookingPaidCalendarInterface
    | TwoStepPaymentFormStepInterface,
) {
  const rollbar = useRollbar()
  const { errors, setErrors, isLoading, submit } = usePaymentSubmit(entity)
  const purchaseProcessId = usePayment(getPurchaseProcessId)
  const pageId = usePage(getPageId)
  const { Razorpay, generateOptions, isRazorpayReady, isRazorpayFetching } =
    useRazorpay({ setErrors })
  const pricePlan = usePayment(getActivePricePlan)
  const bump = usePayment(getCheckedBump)
  const [bumpOptions, setBumpOptions] = useState<RazorpayOptions | null>(null)
  const product = usePayment(paymentSelectors.getProduct)

  const bumpPlan = bump?.pricePlans ? bump.pricePlans[0] : null
  const productBump = bump?.product ? bump.product : null

  useEffect(() => {
    if (bumpOptions) {
      const razorpay = new Razorpay(bumpOptions)
      razorpay.open()
    }
  }, [bumpOptions, Razorpay])

  const proceedBumpPlan = async (
    mainPurchaseResponse: RazorpayBuyOfferResponse,
  ) => {
    // process bump
    const bumpOptions = generateOptions(mainPurchaseResponse, bumpPlan, true)
    bumpOptions.modal = {
      ondismiss: () => {
        const redirect =
          mainPurchaseResponse.redirect || mainPurchaseResponse.redirectUrl
        window.location.assign(redirect)
      },
    }
    await new Promise(() =>
      // without timeout RP popup is not opened sometimes
      // and it doesn't matter if we use useState or not
      setTimeout(() => setBumpOptions(bumpOptions), 1000),
    )
  }

  const proceedProductBumpPlan = async (
    mainPurchaseResponse: RazorpayBuyOfferResponse,
  ) => {
    // process product bump
    const productBumpOptions = generateOptions(
      mainPurchaseResponse,
      null,
      true,
      productBump,
    )
    productBumpOptions.modal = {
      ondismiss: () => {
        const redirect =
          mainPurchaseResponse.redirect || mainPurchaseResponse.redirectUrl

        window.location.assign(redirect)
      },
    }
    await new Promise(() =>
      // without timeout RP popup is not opened sometimes
      // and it doesn't matter if we use useState or not
      setTimeout(() => setBumpOptions(productBumpOptions), 1000),
    )
  }

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault()

    submit(
      async body => {
        const { data: mainPurchaseResponse } = await buyMainOffer(
          pageId,
          purchaseProcessId,
          {
            payment_form: body,
          },
        )
        const isHundredPercentCoupon =
          isHundredPercentCouponBasedOnMainOfferResponse(
            pricePlan,
            product,
            mainPurchaseResponse,
          )

        if (!isHundredPercentCoupon) {
          //case when we need to show all modals -> then redirect
          const options = generateOptions(
            mainPurchaseResponse,
            pricePlan,
            false,
            product,
          )
          if (bumpPlan) {
            options.handler = async () => {
              await proceedBumpPlan(mainPurchaseResponse)
            }
          }
          if (productBump) {
            options.handler = async () => {
              await proceedProductBumpPlan(mainPurchaseResponse)
            }
          }

          if (isRazorpayReady) {
            const razorpay = new Razorpay(options)
            razorpay.open()
          } else {
            throw new Error('form submitted before razorpay was ready')
          }

          return
        } else if (bumpPlan || productBump) {
          //case when we need to show only bump modal -> then redirect
          if (bumpPlan) {
            await proceedBumpPlan(mainPurchaseResponse)
          }
          if (productBump) {
            await proceedProductBumpPlan(mainPurchaseResponse)
          }
        } else {
          //case when we need to only redirect
          const redirect =
            mainPurchaseResponse.redirectUrl || mainPurchaseResponse.redirect
          if (redirect) {
            window.location.assign(redirect)
            return
          }
        }
      },
      (error, body) => {
        if (
          !(error instanceof BadRequest) &&
          !(error instanceof NetworkError) &&
          !(error instanceof InternalError)
        ) {
          if (error instanceof Error) {
            rollbar.captureEvent(body!, 'debug')
            rollbar.error('Razorpay error', error as Error, body)
          }
        }
      },
    )
  }

  return {
    errors,
    isLoading,
    isRazorpayReady,
    isRazorpayFetching,
    handleSubmit,
  }
}
